import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ProgressCircle } from '@dynatrace/strato-components/content';
import { Flex } from '@dynatrace/strato-components/layouts';

import { loadingStateCSS } from './LoadingState.sty.js';
import type { TableOptions } from '../hooks/useTable/types.js';
import { dataTableV2HeaderHeight } from '../variables.sty.js';

/**
 * Prop extension for the loading state on the DataTableV2.
 * @public
 */
export interface DataTableV2LoadingStateProps {
  /**
   * Displays a loading indicator while the table's columns and/or data are being loaded.
   *
   * @defaultValue false
   */
  loading?: boolean;
}

/**
 * Extension interface to extend the tanstack table state with the loading state.
 * @internal
 */
export interface DataTableV2LoadingState {
  /**
   * Table state extension for loading state
   */
  loading?: boolean;
}

/**
 * Configuration hook for the DataTableV2 LoadingState feature.
 * @internal
 */
export function useLoadingState<TData>(
  props: DataTableV2LoadingStateProps,
  options: TableOptions<TData>,
) {
  options.state = { ...options.state, loading: props.loading };
}

/**
 * Small wrapper that makes sure to only render the defined actions when the menu
 * is open.
 * @internal
 */
export function LoadingState(props: { headerHeight: number }) {
  const { headerHeight } = props;
  const loadingContainerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const intl = useIntl();

  useEffect(() => {
    if (!loadingContainerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        setHeight(entries[0].contentRect.height);
      }
    });
    resizeObserver.observe(loadingContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Flex
      ref={loadingContainerRef}
      justifyContent="center"
      alignItems="center"
      className={loadingStateCSS}
      style={assignInlineVars({
        [dataTableV2HeaderHeight]: `${headerHeight}px`,
      })}
    >
      <ProgressCircle
        size={height !== undefined && height > 120 ? 'large' : 'small'}
        aria-label={intl.formatMessage({
          defaultMessage: 'Loading data',
          id: 'EWfCcasdyt/QUnjk',
          description: 'Label for DataTable loading state.',
        })}
      />
    </Flex>
  );
}
